import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { setActionToConfirm, setSnackbar } from '../../../../store';
import * as actions from '../../../../store/index';
import { fetchDelete, fetchGet, fetchPut } from '../../../../helpers/fetching';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Tooltip,
  Typography,
  IconButton,
  Divider,
  Chip,
  TextField,
  Fab,
} from '@material-ui/core';
import { Delete, Edit, Spellcheck } from '@material-ui/icons';
import ClearIcon from '@material-ui/icons/Clear';
import animation from '../../../../resources/processing_animation.gif';
import AddIcon from '@material-ui/icons/Add';
import RefreshIcon from '@material-ui/icons/Refresh';
import DoneIcon from '@material-ui/icons/Done';
import BlockIcon from '@material-ui/icons/Block';
import ADIcon from '../../../../resources/icons/active_directory.png';
import EnhancedTableHead from './Common/EnhancedTableHead';
import { Search, Clear } from '@material-ui/icons';
import { InputAdornment } from '@material-ui/core';

const useStyles = makeStyles((theme) => ({
  chipSmall: {
    margin: '6px',
  },

  helperText: {
    color: '#ff7676',
    fontSize: '10px',
    margin: '1px',
    whiteSpace: 'wrap',
    maxWidth: '210px',
  },

  add_user_container: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '10px',
    minHeight: '40px',
    width: '100%',
  },

  tooltip_text: {
    letterSpacing: '0.1rem',
    textTransform: 'uppercase',
    fontSize: '12px',
  },

  tooltip_text_lowercase: {
    letterSpacing: '0.1rem',
    fontSize: '12px',
  },

  table_placeholder: {
    color: 'gray',
    padding: '15%',
    border: 'none',
    fontSize: '10px',
    fontStyle: 'italic',
    padding: '14px 10px 10px',
    borderBottom: 'unset',
  },

  tableGeneral: {
    padding: '2px',
  },

  tableGeneralTextField: {
    padding: '10px 6px 6px',
  },

  newPasswordField: {
    width: '210px',
  },

  tableRowStriped: {
    border: '1px solid red',
    '&:nth-child(odd)': {
      backgroundColor: '#494949',
    },
  },

  institutionDropdown: {
    '& .MuiTypography-root': {
      fontSize: '14px',
    },
    '& .MuiMenuItem-root': {
      minHeight: 20,
    },
    '& .MuiCheckbox-root': {
      padding: '1px',
    },
  },

  saveIconList: {
    margin: '6px 10px',
    height: '26px',
    width: '26px',
    minHeight: '26px',
    minWidth: '26px',
    backgroundColor: theme.palette.accents.main,
    '&:hover': {
      backgroundColor: theme.palette.accents.dark,
      boxShadow: '0px 0px 5px 0px #d7c2c2',
    },
  },
}));

function UsersPanel(props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const classes = useStyles();
  const notifications = useSelector((state) => state.gui.notifications);
  const { premium_features } = useSelector((state) => state.global_settings);

  const currentRole = localStorage.getItem('role');

  const [users, setUsers] = useState([]);
  const [order, setOrder] = useState('ASC');
  const [orderBy, setOrderBy] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const isMountedRef = useRef(true);

  const { diagnostics_microcalc_module, diagnostics_opacities_module } = premium_features;
  const hasDiagnosticsModule = diagnostics_microcalc_module || diagnostics_opacities_module;

  const heads = useMemo(
    () => [
      { key: 'status', label: '', align: 'center' },
      { key: 'username', label: t('users.username'), align: 'left' },
      { key: 'first_name', label: t('users.first_name'), align: 'left' },
      { key: 'last_name', label: t('users.last_name'), align: 'left' },
      { key: 'email', label: t('users.email'), align: 'left' },
      { key: 'active', label: t('users.active'), align: 'left' },
      { key: 'statistics_opt_out', label: t('users.statistics_opt_out'), align: 'left' },
      { key: 'aliases', label: t('users.dicomTag'), align: 'left' },
      { key: 'role', label: t('users.role'), align: 'left' },
      { key: 'institution', label: t('ResultView.institution'), align: 'center' },
      {
        key: 'annotation_permission',
        label: t('users.annotator'),
        align: 'center',
      },
      {
        key: 'diagnostics_permission',
        label: t('UserPanel.b_diagnostics'),
        align: 'center',
        hasDiagnosticsModule: hasDiagnosticsModule,
      },
      { key: 'actions', label: t('users.actions'), align: 'center' },
    ],
    []
  );

  const fetchUsers = async () => {
    if (!isMountedRef.current) return;
    setLoading(true);
    try {
      const response = await fetchGet('users');
      if (response.success && isMountedRef.current) {
        setUsers(response.data.filter((user) => user.username !== 'admin'));
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [props.newRow, props.editUser]);

  const updateUser = async (user, value, moduleRow) => {
    const updateBody = {
      [moduleRow]: typeof value === 'number' || typeof value === 'boolean' ? !value : value,
    };
    try {
      const response = await fetchPut(`user/${user}`, updateBody);
      if (response.success && isMountedRef.current) {
        fetchUsers();
        dispatch(
          setSnackbar({
            msg: 'UserPanel.snack_user_updated',
            severity: 'success',
          })
        );
      } else if (isMountedRef.current) {
        dispatch(
          setSnackbar({
            msg: 'UserPanel.snack_user_failed',
            severity: 'error',
          })
        );
      }
    } catch (error) {
      console.error('Failed to update user', error);
      if (isMountedRef.current) {
        dispatch(
          setSnackbar({
            msg: 'UserPanel.snack_user_failed',
            severity: 'error',
          })
        );
      }
    }
    if (isMountedRef.current) {
      setLoading(false);
    }
  };

  const updatePermission = async (username, permission, current_status) => {
    let response;
    try {
      if (current_status) {
        response = await fetchDelete('permissions', {
          username,
          permission,
        });
      } else {
        response = await fetchPut('permissions', {
          username,
          permission,
        });
      }

      if (response.success && isMountedRef.current) {
        fetchUsers();
        dispatch(
          setSnackbar({
            msg: 'UserPanel.snack_user_updated',
            severity: 'success',
          })
        );
      } else {
        dispatch(
          setSnackbar({
            msg: 'UserPanel.permission_failed',
            severity: 'error',
          })
        );
      }
    } catch (error) {
      console.error('Failed to update permission', error);
      if (isMountedRef.current) {
        dispatch(
          setSnackbar({
            msg: 'UserPanel.permission_failed',
            severity: 'error',
          })
        );
      }
    }
  };

  const deleteUser = async (username) => {
    if (!username) {
      if (isMountedRef.current) {
        dispatch(
          setSnackbar({
            msg: 'UserPanel.snack_not_selected',
            severity: 'warning',
          })
        );
      }
      return;
    }
    const response = await fetchDelete(`user/${username}`, { username });
    if (response.success && isMountedRef.current) {
      setUsers((prevState) => prevState.filter((u) => u.username !== username));
      dispatch(
        setSnackbar({
          msg: 'UserPanel.snack_not_selected',
          severity: 'success',
        })
      );
      fetchUsers();
    } else if (isMountedRef.current) {
      dispatch(
        setSnackbar({
          msg: 'UserPanel.snack_delete_failed',
          severity: 'error',
        })
      );
    }
  };

  const descendingComparator = (a, b, orderBy) => {
    if (b[orderBy] < a[orderBy]) return -1;
    if (b[orderBy] > a[orderBy]) return 1;
    return 0;
  };

  const getComparator = (order, orderBy) => {
    return order === 'DESC'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  };

  const handleRequestSort = (_, property) => {
    const isAsc = orderBy === property && order === 'ASC';
    setOrder(isAsc ? 'DESC' : 'ASC');
    setOrderBy(property);
  };

  const closeUsersDialog = () => {
    dispatch(setActiveDialog(null));
    dispatch(deleteNotification(notifications.map(({ id }) => id)));
  };

  const onADRefresh = async () => {
    const response = await fetchGet('ad_refresh', 'bbox');

    if (response.success && isMountedRef.current) {
      await fetchUsers();
      dispatch(
        actions.setSnackbar({
          msg: 'UserPanel.users_synchronized',
          severity: 'info',
        })
      );
    } else if (isMountedRef.current) {
      dispatch(
        actions.setSnackbar({
          msg: 'users.users_error',
          severity: 'error',
          msgParams: { response_msg: response.msg },
        })
      );
    }
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  const filteredUsers = useMemo(() => {
    if (!searchTerm || searchTerm.length < 2) {
      return users;
    }

    const searchLower = searchTerm.toLowerCase();
    return users.filter((user) => {
      // Search in username
      const usernameMatch = user.displayed_name?.toLowerCase().includes(searchLower);

      // Search in aliases
      const aliasesMatch = user.aliases?.toLowerCase().includes(searchLower);

      // Search in institution
      const institutionMatch = user.institution_name?.toLowerCase().includes(searchLower);

      return usernameMatch || aliasesMatch || institutionMatch;
    });
  }, [users, searchTerm]);

  return (
    <>
      {/* Search Field */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <TextField
          size='small'
          placeholder={t('Layout.search')}
          value={searchTerm}
          onChange={handleSearchChange}
          variant='outlined'
          style={{ width: '300px' }}
          InputProps={{
            style: {
              backgroundColor: theme.palette.background.mainDark,
              color: theme.palette.text.primary,
              border: `1px solid ${theme.palette.divider}`,
              padding: '4px',
              fontSize: theme.fonts.responsiveMedium,
              letterSpacing: '0.1rem',
            },
            startAdornment: (
              <InputAdornment position='start'>
                <Search fontSize='small' />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position='end' style={{ cursor: 'pointer' }} onClick={clearSearch}>
                {searchTerm && <Clear fontSize='small' />}
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {loading ? (
        <div
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
          }}>
          <img
            style={{ width: '100px', height: '100px', borderRadius: '50%' }}
            src={animation}
            alt='logoanimation'
          />
          <Typography style={{ fontSize: '10px', color: 'gray', fontStyle: 'italic' }}>
            {t('Dashboard.loading')}
          </Typography>
        </div>
      ) : (
        <Paper sx={{ width: '100%', mb: 2 }}>
          <TableContainer>
            <Table sx={{ minWidth: 750 }} aria-labelledby='users_tableTitle' size={'medium'} stickyHeader>
              <EnhancedTableHead
                order={order}
                orderBy={orderBy}
                onRequestSort={handleRequestSort}
                heads={heads}
                rowCount={filteredUsers.length}
              />
              {filteredUsers.length > 0 ? (
                <TableBody>
                  {filteredUsers.sort(getComparator(order, orderBy)).map((row, i) => (
                    <TableRow
                      hover
                      tabIndex={-1}
                      key={`${row.username}-${i}`}
                      className={classes.tableRowStriped}>
                      {heads.map((head) => {
                        if (head.key === 'actions')
                          return currentRole === 'admin' ||
                            (currentRole === 'supervisor' && row.role !== 'supervisor') ? (
                            <TableCell
                              className={classes.tableGeneral}
                              align={head.align}
                              key={`${row.username}-${head.key}`}>
                              <Tooltip
                                arrow
                                placement='top-start'
                                title={
                                  <Typography
                                    style={{
                                      letterSpacing: '0.1rem',
                                      textTransform: 'uppercase',
                                      fontSize: theme.fonts.responsiveMedium,
                                      fontWeight: 'bold',
                                    }}>
                                    {t('UserPanel.edit')}
                                  </Typography>
                                }>
                                <span>
                                  <IconButton
                                    size='small'
                                    disabled={row.username === 'service'}
                                    onClick={() => props.setEditUser(row)}>
                                    <Edit fontSize='small' />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip
                                arrow
                                placement='bottom'
                                title={
                                  <Typography
                                    style={{
                                      letterSpacing: '0.1rem',
                                      textTransform: 'uppercase',
                                      fontSize: theme.fonts.responsiveMedium,
                                      fontWeight: 'bold',
                                    }}>
                                    {t('UserPanel.delete')}
                                  </Typography>
                                }>
                                <span>
                                  <IconButton
                                    size='small'
                                    disabled={row.username === 'service'}
                                    onClick={() =>
                                      dispatch(
                                        setActionToConfirm({
                                          action: () => deleteUser(row.username),
                                          text: `${t('UserPanel.confirm_delete')} ${row.username}?`,
                                        })
                                      )
                                    }>
                                    <Delete fontSize='small' />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </TableCell>
                          ) : (
                            <TableCell
                              className={classes.tableGeneral}
                              align={head.align}
                              key={`${row.username}-${head.key}`}
                            />
                          );

                        if (head.key === 'active')
                          return (
                            <TableCell
                              className={classes.tableGeneral}
                              align={head.align}
                              key={`${row.username}-${head.key}`}>
                              <Tooltip
                                arrow
                                placement='bottom'
                                title={
                                  <>
                                    <Typography
                                      style={{
                                        letterSpacing: '0.1rem',
                                        textTransform: 'uppercase',
                                        fontSize: theme.fonts.responsiveMedium,
                                        fontWeight: 'bold',
                                      }}>
                                      {!!row.active ? t('UserPanel.enable') : t('UserPanel.disable')}
                                    </Typography>
                                    <Divider style={{ margin: '3% 0' }} />
                                    <Typography
                                      style={{
                                        letterSpacing: '0.1rem',
                                        fontSize: theme.fonts.responsiveMedium,
                                      }}>
                                      {!!row.active
                                        ? t('UserPanel.enable_click')
                                        : t('UserPanel.disable_click')}
                                    </Typography>
                                  </>
                                }>
                                <span>
                                  {!!row.active ? (
                                    <Chip
                                      clickable
                                      variant='outlined'
                                      label={t('UserPanel.enable')}
                                      size='small'
                                      disabled={
                                        (currentRole === 'supervisor' && row.role === 'supervisor') ||
                                        row.username === 'service'
                                      }
                                      onClick={() => updateUser(row.username, row.active, 'active')}
                                      style={{
                                        color: '#18cc73',
                                        border: `1px solid #18cc73`,
                                      }}
                                    />
                                  ) : (
                                    <Chip
                                      clickable
                                      variant='default'
                                      label={t('UserPanel.disable')}
                                      size='small'
                                      color='primary'
                                      disabled={currentRole === 'supervisor' && row.role === 'supervisor'}
                                      onClick={() => updateUser(row.username, row.active, 'active')}
                                    />
                                  )}
                                </span>
                              </Tooltip>
                            </TableCell>
                          );

                        if (head.key === 'statistics_opt_out')
                          return (
                            <TableCell
                              className={classes.tableGeneral}
                              align={head.align}
                              key={`${row.username}-${head.key}`}>
                              <Tooltip
                                arrow
                                placement='bottom'
                                title={
                                  <>
                                    <Typography
                                      style={{
                                        letterSpacing: '0.1rem',
                                        textTransform: 'uppercase',
                                        fontSize: theme.fonts.responsiveMedium,
                                        fontWeight: 'bold',
                                      }}>
                                      {row.statistics_opt_out
                                        ? t('UserPanel.statistics_opt_out_enabled')
                                        : t('UserPanel.statistics_opt_out_disabled')}
                                    </Typography>
                                    <Divider style={{ margin: '3% 0' }} />
                                    <Typography
                                      style={{
                                        letterSpacing: '0.1rem',
                                        fontSize: theme.fonts.responsiveMedium,
                                      }}>
                                      {row.statistics_opt_out
                                        ? t('UserPanel.statistics_opt_out_disable_click')
                                        : t('UserPanel.statistics_opt_out_enable_click')}
                                    </Typography>
                                  </>
                                }>
                                <span>
                                  <Chip
                                    clickable
                                    variant={row.statistics_opt_out ? 'outlined' : 'default'}
                                    label={
                                      row.statistics_opt_out
                                        ? t('UserPanel.statistics_opt_out_enabled_short')
                                        : t('UserPanel.statistics_opt_out_disabled_short')
                                    }
                                    size='small'
                                    color={row.statistics_opt_out ? 'default' : 'primary'}
                                    disabled={currentRole === 'supervisor' && row.role === 'supervisor'}
                                    onClick={() =>
                                      updateUser(
                                        row.username,
                                        !!row.statistics_opt_out,
                                        'statistics_opt_out'
                                      )
                                    }
                                    style={
                                      row.statistics_opt_out
                                        ? {
                                            color: theme.palette.texts.primary,
                                            border: `1px solid ${theme.palette.texts.primary}`,
                                          }
                                        : {}
                                    }
                                  />
                                </span>
                              </Tooltip>
                            </TableCell>
                          );

                        if (head.key === 'annotation_permission')
                          return (
                            <TableCell
                              className={classes.tableGeneral}
                              align={head.align}
                              key={`${row.username}-${head.key}`}>
                              <Tooltip
                                arrow
                                placement='bottom'
                                title={
                                  <>
                                    <Typography
                                      style={{
                                        letterSpacing: '0.1rem',
                                        textTransform: 'uppercase',
                                        fontSize: theme.fonts.responsiveMedium,
                                        fontWeight: 'bold',
                                      }}>
                                      {!!row.annotation_permission
                                        ? t('UserPanel.annotating')
                                        : t('UserPanel.no_annotating')}
                                    </Typography>
                                    <Divider style={{ margin: '3% 0' }} />
                                    <Typography
                                      style={{
                                        letterSpacing: '0.1rem',
                                        fontSize: theme.fonts.responsiveMedium,
                                      }}>
                                      {!!row.annotation_permission
                                        ? t('UserPanel.annotating_click')
                                        : t('UserPanel.no_annotating_click')}
                                    </Typography>
                                  </>
                                }
                                aria-label='add'>
                                <span>
                                  <IconButton
                                    size='small'
                                    disabled={currentRole === 'supervisor' && row.role === 'supervisor'}
                                    onClick={() =>
                                      updatePermission(
                                        row.username,
                                        'annotation',
                                        !!row.annotation_permission
                                      )
                                    }
                                    style={{
                                      color: !!row.annotation_permission ? '#18cc73' : 'inherit',
                                    }}>
                                    {!!row.annotation_permission ? (
                                      <Spellcheck fontSize='small' />
                                    ) : (
                                      <BlockIcon fontSize='small' />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </TableCell>
                          );

                        if (head.key === 'diagnostics_permission')
                          return (
                            <TableCell
                              className={classes.tableGeneral}
                              align={head.align}
                              key={`${row.username}-${head.key}`}>
                              <Tooltip
                                arrow
                                placement='bottom'
                                title={
                                  <>
                                    {hasDiagnosticsModule ? (
                                      <>
                                        <Typography
                                          style={{
                                            letterSpacing: '0.1rem',
                                            textTransform: 'uppercase',
                                            fontSize: theme.fonts.responsiveMedium,
                                            fontWeight: 'bold',
                                          }}>
                                          {!!row.diagnostics_permission
                                            ? t('UserPanel.diagnostics')
                                            : t('UserPanel.no_diagnostics')}
                                        </Typography>
                                        <Divider style={{ margin: '3% 0' }} />
                                        <Typography
                                          style={{
                                            letterSpacing: '0.1rem',
                                            fontSize: theme.fonts.responsiveMedium,
                                          }}>
                                          {!!row.diagnostics_permission
                                            ? t('UserPanel.diagnostics_click')
                                            : t('UserPanel.no_diagnostics_click')}
                                        </Typography>
                                      </>
                                    ) : (
                                      <Typography>{t('UserPanel.no_diagnostics')}</Typography>
                                    )}
                                  </>
                                }
                                aria-label='add'>
                                <span>
                                  <IconButton
                                    size='small'
                                    disabled={
                                      (currentRole === 'supervisor' && row.role === 'supervisor') ||
                                      !hasDiagnosticsModule
                                    }
                                    onClick={() =>
                                      hasDiagnosticsModule
                                        ? updatePermission(
                                            row.username,
                                            'diagnostics',
                                            !!row.diagnostics_permission
                                          )
                                        : null
                                    }
                                    style={{
                                      color: !!row.diagnostics_permission ? '#18cc73' : 'inherit',
                                      opacity: hasDiagnosticsModule ? 1 : 0.2,
                                    }}>
                                    {row.diagnostics_permission ? (
                                      <DoneIcon fontSize='small' />
                                    ) : (
                                      <ClearIcon fontSize='small' />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </TableCell>
                          );

                        if (head.key === 'aliases')
                          return (
                            <TableCell
                              className={classes.tableGeneral}
                              align={head.align}
                              key={`${row.username}-${head.key}`}>
                              <div>
                                {row?.aliases?.split(';').map((alias, j) => (
                                  <Chip
                                    key={`${row.username}-${alias}-${j}`}
                                    className={classes.chipSmall}
                                    size='small'
                                    label={alias.trim() ? alias.trim() : '-'}
                                  />
                                ))}
                              </div>
                            </TableCell>
                          );

                        if (head.key === 'institution')
                          return (
                            <TableCell key={`${row.username}-${head.key}`} className={classes.tableGeneral}>
                              <div
                                style={{
                                  overflowX: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  textAlign: 'center',
                                  maxHeight: '100px',
                                  overflowY: 'scroll',
                                }}>
                                {row?.institution_name
                                  ? row?.institution_name?.split(';').map((institutionName, index) => (
                                      <Typography
                                        key={`${row.institution_name}-${institutionName}-${index}-${i}`}
                                        style={{
                                          fontSize: theme.fonts.responsiveMediumBig,
                                        }}>
                                        {institutionName.trim()}
                                      </Typography>
                                    ))
                                  : row.role === 'radiologist'
                                  ? t('Filters.all_institutions_label')
                                  : '-'}
                              </div>
                            </TableCell>
                          );

                        if (head.key === 'status')
                          return !!row?.ldap_name ? (
                            <TableCell
                              className={classes.tableGeneral}
                              align='center'
                              key={`${row.username}-${head.key}`}>
                              <Tooltip
                                arrow
                                placement='top'
                                title={
                                  <Typography
                                    style={{
                                      letterSpacing: '0.1rem',
                                      textTransform: 'uppercase',
                                      fontSize: theme.fonts.responsiveMedium,
                                      fontWeight: 'bold',
                                    }}>
                                    AD (Active Directory)
                                  </Typography>
                                }>
                                <img
                                  src={ADIcon}
                                  alt='AD Icon'
                                  style={{
                                    width: '26px',
                                    height: '26px',
                                  }}
                                />
                              </Tooltip>
                            </TableCell>
                          ) : (
                            <TableCell
                              className={classes.tableGeneral}
                              align={head.align}
                              key={`${row.username}-${head.key}`}
                            />
                          );

                        if (head.key === 'username') {
                          return (
                            <TableCell
                              key={`${i}${row.username}-${head.key}${row.displayed_name}`}
                              className={classes.tableGeneral}
                              align={head.align}>
                              <Tooltip
                                title={
                                  <Typography className={classes.tooltip_text_lowercase} color='inherit'>
                                    {`${t('UserPanel.login_name')}: ${row.username}`}
                                  </Typography>
                                }
                                arrow
                                interactive
                                placement='left'>
                                <Typography style={{ fontSize: theme.fonts.responsiveMediumBig }}>
                                  {row.displayed_name}
                                </Typography>
                              </Tooltip>
                            </TableCell>
                          );
                        }

                        if (head.key === 'role') {
                          return (
                            <TableCell
                              key={`${i}${row.username}-${head.key}${row.role}`}
                              className={classes.tableGeneral}
                              align={head.align}>
                              <Tooltip
                                title={
                                  <Typography className={classes.tooltip_text_lowercase} color='inherit'>
                                    {`${t('UserPanel.login_name')}: ${row.username}`}
                                  </Typography>
                                }
                                arrow
                                interactive
                                placement='left'>
                                <Typography style={{ fontSize: theme.fonts.responsiveMediumBig }}>
                                  {t(`users.${row.role}`, { defaultValue: row.role })}{' '}
                                  {row.qa_responsible ? '(QA)' : ''}
                                </Typography>
                              </Tooltip>
                            </TableCell>
                          );
                        }

                        return (
                          <TableCell
                            className={classes.tableGeneral}
                            align={head.align}
                            key={`${head.key}-${row.username}`}>
                            <Typography style={{ fontSize: theme.fonts.responsiveMediumBig }}>
                              {row[head.key] && row[head.key] !== undefined && row[head.key] !== 0
                                ? row[head.key]
                                : '--'}
                            </Typography>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              ) : (
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <Typography className={classes.table_placeholder}>{t('UserPanel.nothing')}</Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}
            </Table>
          </TableContainer>

          <Box className={classes.add_user_container}>
            <Tooltip
              title={
                <Typography className={classes.tooltip_text} color='inherit'>
                  {t('UserPanel.refresh_ad_users')}
                </Typography>
              }
              arrow
              interactive
              placement='left'>
              <span>
                <Fab
                  size='small'
                  color='secondary'
                  aria-label='refresh'
                  onClick={onADRefresh}
                  disabled={props.newRow}>
                  <RefreshIcon fontSize='small' />
                </Fab>
              </span>
            </Tooltip>
            <Tooltip
              title={
                <Typography className={classes.tooltip_text} color='inherit'>
                  {t('users.add_user')}
                </Typography>
              }
              arrow
              interactive
              placement='left'>
              <span>
                <Fab
                  size='small'
                  color='secondary'
                  aria-label='add'
                  onClick={() => props.setNewRow(true)}
                  disabled={props.newRow}
                  style={{
                    marginLeft: '10px',
                  }}>
                  <AddIcon fontSize='small' />
                </Fab>
              </span>
            </Tooltip>
          </Box>
        </Paper>
      )}
    </>
  );
}

export default UsersPanel;
